import React from 'react';
import cx from 'classnames';
import { Icon } from '@treelab/panda';

import { ARROW_RIGHT } from '../base/icons';
import { Typography } from '../../components/Typography';
import { TypographyStyle, TypographyWeight } from '../types';

interface IAccordionProps {
  /** Optional passed classname. */
  className?: string;
  /** Optional children to pass in, for example if we want text */
  children?: React.ReactNode;
  /** Is accordion default open */
  isDefaultOpen?: boolean;
  /** Small is usually in sidebar only */
  isSmall?: boolean;
  /** Reverse color */
  isReverse?: boolean;
  /** Padding size for background color adjustment. Only is available for small */
  paddingSize?: number;
  /** Mandatory title */
  title: string;
  /** Optional title */
  subtitle?: string;
  /** Optional renderer for things like search component */
  renderHeaderChildren?: () => void;
}

interface IState {
  isOpen: boolean;
}

export class Accordion extends React.PureComponent<IAccordionProps, IState> {
  constructor(props: IAccordionProps) {
    super(props);

    this.state = {
      isOpen: props.isDefaultOpen === undefined ? true : props.isDefaultOpen,
    };
  }

  render() {
    const {
      className,
      children,
      isReverse,
      isSmall,
      paddingSize,
      subtitle,
      title,
      renderHeaderChildren,
    } = this.props;
    const { isOpen } = this.state;

    const combinedClass = cx({
      'panda-accordion': true,
      'panda-accordion--not-reverse': !isReverse,
      'panda-accordion--reverse': !!isReverse,
      'panda-accordion--is-open': isOpen,
      'panda-accordion--is-open--reverse': !!isReverse && isOpen,
      [className || '']: !!className,
    });
    const combinedHeaderClass = cx({
      'panda-accordion-header': true,
      'panda-accordion-header--border-bottom': !isSmall,
    });
    const combinedSubtitleClass = cx({
      'panda-accordion--subtitle': true,
      'panda-accordion--color-sensitive': true,
    });
    const inlineContainerStyle =
      (paddingSize && {
        margin: `0 -${paddingSize}px`,
        padding: `0 ${paddingSize}px`,
      }) ||
      {};

    return (
      <div className={combinedClass} style={inlineContainerStyle}>
        <div className={combinedHeaderClass} onClick={() => this.setState({ isOpen: !isOpen })}>
          <div className="text-ellipsis">
            <Typography
              type={isSmall ? TypographyStyle.P1 : TypographyStyle.H4}
              className="panda-accordion--color-sensitive"
              style={{ display: 'inline' }}
            >
              {title}
            </Typography>
            <Typography
              type={isSmall ? TypographyStyle.P2 : TypographyStyle.P1}
              weight={TypographyWeight.Light}
              className={combinedSubtitleClass}
            >
              {subtitle}
            </Typography>
          </div>
          <div className="panda-accordion-header-children">
            {renderHeaderChildren && renderHeaderChildren()}
            <Icon
              icon={ARROW_RIGHT}
              className="panda-accordion--color-sensitive panda-accordion--arrow-icon"
              size={{ height: 14, width: 14 }}
            />
          </div>
        </div>

        {isOpen && <div className="panda-accordion-children-container">{children}</div>}
      </div>
    );
  }
}
